const GA_MEASUREMENT_ID = "G-NY9747WYM0";
const GA_CONFIG = { cookie_flags: "samesite=none;secure", send_page_view: false };

type Params = Record<string, unknown>;

export interface AnalyticsContext {
  operator_id: string;
  theme: string;
  language: string;
  currency: string;
  channel: string;
  entry: string;
}

interface Origin {
  origin_screen: string;
  origin_button: string;
}

let enabled = false;
let context: Params = {};
let origin: Origin = { origin_screen: "unknown", origin_button: "unknown" };
let roundOpenedAt: number | undefined;

export function isTerminalTraffic(): boolean {
  const navigationKey = new URLSearchParams(window.location.search).get("navigationKey") ?? "";
  return window.isTerminal || window.location.pathname.startsWith("/terminal") || navigationKey.startsWith("terminal");
}

// Only origin + path: the query string carries the operator token and player id.
function cleanLocation(): string {
  return window.location.origin + window.location.pathname;
}

export function initAnalytics(ctx: AnalyticsContext, playerId: string): void {
  if (enabled || isTerminalTraffic()) {
    return;
  }
  enabled = true;
  context = { ...ctx, logged_in: false };
  if (ctx.entry !== "direct") {
    origin = { origin_screen: "deeplink", origin_button: ctx.entry };
  }

  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("set", { page_location: cleanLocation() });
  window.gtag("config", GA_MEASUREMENT_ID, GA_CONFIG);
  window.gtag("set", "user_properties", { operator_id: ctx.operator_id, theme: ctx.theme });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  if (ctx.operator_id && playerId) {
    setUserId(ctx.operator_id, playerId);
  }
}

async function setUserId(operatorId: string, playerId: string): Promise<void> {
  if (!window.crypto?.subtle) {
    return;
  }
  const bytes = new TextEncoder().encode(`${operatorId}:${playerId}`);
  const digest = await window.crypto.subtle.digest("SHA-256", bytes);
  const userId = Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  window.gtag("config", GA_MEASUREMENT_ID, { ...GA_CONFIG, user_id: userId });
}

export function setLoggedIn(loggedIn: boolean): void {
  context.logged_in = loggedIn;
}

export function track(event: string, params: Params = {}): void {
  if (!enabled) {
    return;
  }
  window.gtag("event", event, { ...context, ...params });
}

const screens: [RegExp, string][] = [
  [/^\/(\w+\/)?rounds\//, "round"],
  [/^\/mycoupons/, "my_coupons"],
  [/^\/roundhistory/, "round_history"],
  [/^\/coupon\//, "coupon"],
  [/^\/publiccoupon\//, "public_coupon"],
  [/^\/syndicateresult\//, "syndicate_result"],
  [/^\/syndicate\//, "syndicate"],
  [/^\/(playagent|syndicatesforagent)/, "syndicates"],
  [/^\/freebet\//, "freebet"],
  [/^\/help/, "help"],
  [/^\/apibetting/, "api_betting"],
];

export function screenName(pathname: string): string {
  return screens.find(([pattern]) => pattern.test(pathname))?.[1] ?? "landing";
}

export function trackPageView(pathname: string): void {
  if (!enabled) {
    return;
  }
  const pageLocation = cleanLocation();
  window.gtag("set", { page_location: pageLocation });
  track("page_view", { page_title: screenName(pathname), page_location: pageLocation });
}

export function setOrigin(screen: string, button: string): void {
  origin = { origin_screen: screen, origin_button: button };
}

export function trackRoundOpened(roundId: number | string, product: string): void {
  roundOpenedAt = Date.now();
  track("round_opened", { round_id: roundId, product, ...origin });
}

export function trackCouponTool(tool: string, params: Params = {}): void {
  track("coupon_tool_used", { tool, ...params });
}

export function trackBeginCheckout(params: { round_id: number | string; value: number; currency: string; rows?: number; system_type?: string }): void {
  track("begin_checkout", { ...params, ...origin });
}

interface PurchaseData {
  amount: number;
  currency: string;
  roundId: number | string;
  transactionId: string;
  couponShareId?: number;
  couponId?: number;
}

export function trackPurchase(data: PurchaseData, betType: string, systemType: string | undefined, stakeMultiplier: number): void {
  track("purchase", {
    value: data.amount,
    currency: data.currency,
    coupon: data.roundId,
    transaction_id: data.transactionId,
    stakeMultiplier,
    bet_type: betType,
    time_to_bet_ms: roundOpenedAt ? Date.now() - roundOpenedAt : undefined,
    ...origin,
    items: [
      {
        item_id: data.couponShareId,
        coupon: data.couponId,
        item_category: data.roundId,
        price: data.amount,
        item_category2: systemType,
      },
    ],
  });
}
